import {
  ActionButton,
  confirmPanelFormClose,
  DescriptionInputDataKey,
  InputDataDetailsType,
  Modal,
  Padding,
  useMutateInputDataLock,
  useQueryParams,
  useAlertContext,
  DescriptionInputDataExcelUrl,
  loadViewState,
  useEditSessionContext,
  useEditSessionPending,
  useInputDataEditSessionHasDraftsQuery,
  useEditSessionFormRowsContext,
} from '@atlas-frontend/sdk';
import { Button, Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { OpenDirectoryButton } from '../../../common/OpenDirectoryButton';
import { path, type UiPathName } from '../../../paths';
import translations from './InputDataSubjectActions.translations.json';
import { useCallback, useMemo } from 'react';
import { InputDataDescriptionInfo, InputDataVersionInfo } from './types';
import { expand } from 'uri-template-lite';
import { useInputDataDetailsStoreValue } from './InputDataDetailsContext';
import { useFormState } from 'react-hook-form';
import { inputEditError } from './inputErrorUtil';
import { InputDataActionsEditDropdown } from './InputDataActionsEditDropdown';
import { InputDataActionsViewDropdown } from './InputDataActionsViewDropdown';
import * as Icon from 'react-feather';
import { InputDataUpload } from './InputDataUpload';
import { useInputDataActions } from './useInputDataActions';
import { UploadRequestOption } from 'rc-upload/es/interface';
import { OutputSnapshotVersionType } from '@atlas-frontend/sdk';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-sample-actions',
);
addTranslations(translations);

export interface InputDataSubjectActionsProps {
  columnCount?: number | null;
  data?: InputDataDetailsType;
  descriptionData: InputDataDescriptionInfo;
  output?: OutputSnapshotVersionType;
  versionData: InputDataVersionInfo;
  versionId?: number | null;
  canManage: boolean;
  onReload: () => Promise<any>;
  isLoading: boolean;
}

export function InputDataSubjectActions({
  columnCount,
  data,
  descriptionData,
  versionData,
  versionId,
  canManage,
  onReload,
  isLoading: isLoadingData,
}: InputDataSubjectActionsProps) {
  const { t } = useTranslation();
  const [mode, , setMode] = useInputDataDetailsStoreValue('mode');
  const editing = mode === 'edit';
  const disabled = data?.loadingStatus === 'NO_SAMPLE';
  const descriptionId = descriptionData.description?.id;
  const detailParams = useDetailParams(descriptionId);
  const {
    onError,
    onSuccess,
    onExcelUploadSuccess,
    onLoad,
    isLoading: isLoadingAction,
  } = useInputDataActions(onReload);
  const isLoading = isLoadingAction || isLoadingData;

  const hasSample = !inputEditError(
    data?.loadingStatus,
    descriptionData.description?.activeVersionId,
    columnCount,
  );
  const isInputDataLocked =
    descriptionData.description?.locked ||
    descriptionData.description?.sampleLocked;
  const isEditable = hasSample && versionData.version !== 'output';
  const isActive = versionData.version === 'active';

  const { downloadUrl, excelParams } = useMemo(() => {
    if (!detailParams.descriptionId || !data?.valueDate) {
      return { downloadUrl: undefined, excelParams: undefined };
    }

    const excelParams: DescriptionInputDataKey = {
      descriptionId: detailParams.descriptionId,
      valueDate: data?.valueDate,
      dataStatus: versionData.version === 'draft' ? 'draft' : 'active',
      subject: detailParams.subject,
      submittedDataVersionId: versionId ?? undefined,
      sampleId: data?.sampleId ?? undefined,
    };
    return {
      downloadUrl: expand(DescriptionInputDataExcelUrl, excelParams),
      excelParams,
    };
  }, [
    data?.sampleId,
    data?.valueDate,
    detailParams,
    versionData.version,
    versionId,
  ]);

  const openDirectoryButtonOptions = useMemo(() => {
    const options = [];

    const sampleViewState = loadViewState<UiPathName>('sampleView');
    if (sampleViewState) {
      options.push({
        id: 'sampleView',
        path: path.sampleView,
        params: {
          id: sampleViewState.id,
          date: sampleViewState.date,
          type: sampleViewState.type,
        },
        label: t('button.sampleView'),
      });
    } else {
      options.push({
        id: 'sampleDirectory',
        path: path.sampleDirectory,
        label: t('button.sampleView'),
      });
    }

    const inputDataViewState = loadViewState<UiPathName>('inputDataView');
    if (inputDataViewState) {
      options.push({
        id: 'inputDataView',
        path: path.inputDataView,
        params: {
          id: inputDataViewState.id,
          date: inputDataViewState.date,
          type: inputDataViewState.type,
        },
        label: t('button.inputDataView'),
      });
    }

    return options;
  }, [t]);

  return (
    <Flex gap="small">
      {!editing && canManage && isActive && data && descriptionId && (
        <LockButton
          data={data}
          descriptionId={descriptionId}
          lock={!isInputDataLocked}
          disabled={disabled || isLoading}
          onLoad={onLoad}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
      {editing && excelParams && (
        <EditableInputDataUpload
          params={excelParams}
          disabled={isLoading}
          onLoad={onLoad}
          onSuccess={onExcelUploadSuccess}
          onError={onError}
        />
      )}
      <Button
        id="download-samples"
        disabled={disabled || !hasSample}
        style={{ display: 'grid', placeItems: 'center' }}
        icon={<Icon.Download size={18} />}
        href={downloadUrl}
        download
      />
      {editing ? (
        <>
          <CloseInputDataEditButton
            disabled={disabled || isLoading}
            onReload={onReload}
          />
          <ActionButton type="formSubmit" disabled={disabled || isLoading} />
          <InputDataActionsEditDropdown
            onReload={onReload}
            data={data}
            disabled={isLoading}
            isLoading={isLoading}
            description={descriptionData.description}
            version={versionData.version}
          />
        </>
      ) : (
        <>
          <OpenDirectoryButton options={openDirectoryButtonOptions} />
          {canManage && (
            <>
              <ActionButton
                disabled={!isEditable || isLoading}
                type="mainAction"
                layoutType="detail"
                onClick={() => setMode(() => 'edit')}
              />
              <InputDataActionsViewDropdown
                id={descriptionData.description?.id}
                subject={data?.dataSubjectCode}
                valueDate={data?.valueDate}
                version={versionData.version}
                versionId={versionId}
                onReload={onReload}
              />
            </>
          )}
        </>
      )}
    </Flex>
  );
}

function CloseInputDataEditButton({
  disabled,
  onReload,
}: {
  disabled: boolean;
  onReload: () => Promise<any>;
}) {
  const [, , setMode] = useInputDataDetailsStoreValue('mode');
  const [, setSavedVersion] = useInputDataDetailsStoreValue('savedVersion');
  const [, setErrors] = useInputDataDetailsStoreValue('mutationErrors');
  const { sessionId, discardEdits } = useEditSessionContext();

  const handleReset = useCallback(() => {
    if (sessionId) {
      void discardEdits();
    }
    setMode(() => 'view');
    setErrors(undefined);
    setSavedVersion(undefined);
    void onReload();
  }, [discardEdits, onReload, sessionId, setErrors, setMode, setSavedVersion]);

  return <ResetEditButton disabled={disabled} onReset={handleReset} />;
}

function ResetEditButton({
  disabled,
  onReset,
}: {
  disabled: boolean;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const { resetAlert } = useAlertContext();
  const { dirtyFields } = useFormState();
  const { sessionId } = useEditSessionContext();
  const isPending = useEditSessionPending();
  const { unvalidatedRowIndex } = useEditSessionFormRowsContext();
  // The form stays dirty after a save, so ask the edit session whether there are unsaved changes
  const { refetch: fetchHasDrafts } = useInputDataEditSessionHasDraftsQuery(
    { sessionId: sessionId ?? '0' },
    { enabled: false },
  );
  const hasDirtyComments = !!dirtyFields.comments;

  const onToggle = useCallback(async () => {
    const close = () => {
      onReset();
      resetAlert();
    };

    if (hasDirtyComments || isPending || unvalidatedRowIndex !== null) {
      confirmPanelFormClose(close);
      return;
    }

    if (sessionId) {
      const { data, isError } = await fetchHasDrafts();
      if (isError || data?.hasAnyPendingDrafts) {
        confirmPanelFormClose(close);
        return;
      }
    }

    close();
  }, [
    fetchHasDrafts,
    hasDirtyComments,
    isPending,
    onReset,
    resetAlert,
    sessionId,
    unvalidatedRowIndex,
  ]);

  return (
    <Button
      disabled={disabled}
      id="reset-input-data-details"
      onClick={onToggle}
    >
      {t('button.close')}
    </Button>
  );
}

function LockButton({
  data,
  descriptionId,
  lock,
  disabled,
  onLoad,
  onSuccess,
  onError,
}: {
  data: InputDataDetailsType;
  descriptionId: number;
  lock: boolean;
  disabled: boolean;
  onLoad: () => void;
  onSuccess: () => void;
  onError: (err: any) => void;
}) {
  const { t } = useTranslation();
  const { mutateAsync } = useMutateInputDataLock();

  const hasDraftVersion =
    data.descriptions
      ?.flatMap((description) => description.versions ?? [])
      .some((version) => version.draftVersion) ?? false;

  const lockInput = useCallback(
    (lock: boolean) => {
      onLoad();
      return mutateAsync({
        data: {
          lock,
          descriptionId: descriptionId + '',
          groupId: data.dataObjectGroupId
            ? data.dataObjectGroupId + ''
            : undefined,
          valueDate: data.valueDate,
          subject: data.dataSubjectCode,
        },
      })
        .then(onSuccess)
        .catch(onError);
    },
    [data, descriptionId, mutateAsync, onError, onLoad, onSuccess],
  );

  const handleLock = useCallback(
    (lock: boolean) => {
      if (lock || !hasDraftVersion) {
        lockInput(lock);
        return;
      }

      Modal.confirm({
        title: t('unlock-confirm.title'),
        content: t('unlock-confirm.description'),
        footer: (
          <Padding top>
            <Flex justify="end" gap={8}>
              <Button
                id="cancel-lock-input"
                type="default"
                onClick={Modal.destroyAll}
              >
                {t('button.no')}
              </Button>
              <Button
                id="confirm-lock-input"
                type="primary"
                onClick={() => {
                  Modal.destroyAll();
                  lockInput(lock);
                }}
              >
                {t('button.yes')}
              </Button>
            </Flex>
          </Padding>
        ),
      });
    },
    [hasDraftVersion, lockInput, t],
  );

  return (
    <Button
      id={lock ? 'lock-input' : 'unlock-input'}
      disabled={disabled}
      onClick={() => handleLock(lock)}
    >
      {lock ? t('button.lock') : t('button.unlock')}
    </Button>
  );
}

function EditableInputDataUpload({
  params,
  onLoad,
  onSuccess,
  onError,
  disabled,
}: {
  params: DescriptionInputDataKey;
  onLoad: () => void;
  onSuccess: () => void;
  onError: (err: any) => void;
  disabled: boolean;
}) {
  const { insertExcelRows } = useEditSessionContext();

  const handleUpload = useCallback(
    (request: UploadRequestOption) => {
      onLoad();
      const { file } = request;
      return insertExcelRows(file).then(onSuccess).catch(onError);
    },
    [onLoad, insertExcelRows, onSuccess, onError],
  );

  return (
    <InputDataUpload
      onUpload={handleUpload}
      params={params}
      disabled={disabled}
    />
  );
}

function useDetailParams(descriptionId?: number | null) {
  const [{ descriptionId: queryDescriptionId, groupId, subject }] =
    useQueryParams();

  return useMemo(
    () => ({
      descriptionId:
        descriptionId ||
        (queryDescriptionId && Number(queryDescriptionId)) ||
        undefined,
      groupId,
      subject,
    }),
    [descriptionId, groupId, queryDescriptionId, subject],
  );
}
